import * as React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { WithStyles, Theme, createStyles } from '@material-ui/core';
import {
  TextField,
  Paper,
  FormControlLabel,
  Checkbox,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
} from '@material-ui/core';
import { logout, isAdmin, identity } from '../auth/identityAuth';
import HttpService from '../services/HttpService';
import HttpCallConfig from '../models/HttpCallConfig';
import CountryService from 'src/modules/shared/services/CountryService';

const httpService = HttpService.Instance;

const styles = (theme: Theme) =>
  createStyles({
    tcModel: {
      marginTop: theme.spacing.unit * 12,
      marginLeft: theme.spacing.unit * 2,
      marginRight: theme.spacing.unit * 2,
      padding: theme.spacing.unit * 3,
    },
    tcButton: {
      marginRight: theme.spacing.unit,
    },
    fieldsContainer: {
      marginTop: theme.spacing.unit * 2,
    },
    dropFields: {
      display: 'flex',
      flexDirection: 'column',
    },
    textField: {
      margin: theme.spacing.unit * 1.5,
      width: 300,
    },
  });

const addressMapper = (address: any): IAddress => {
  const mappedAddress = {
    country: '',
    city: '',
    postalCode: '',
    street: '',
    contactEmail: '',
    contactName: '',
  };

  if (address) {
    ({
      country: mappedAddress.country,
      city: mappedAddress.city,
      postalCode: mappedAddress.postalCode,
      street: mappedAddress.street,
      contactEmail: mappedAddress.contactEmail,
      contactName: mappedAddress.contactName,
    } = address);
  }

  return mappedAddress;
};

const entityMapper = (entity: any): IEntity => ({
  companyInfo: {
    name: entity.name,
    vat: entity.vat,
  },
  billingAddress: addressMapper(
    entity.addresses.find((address: any) => address.addressTypeId === 2)
  ),
  isSingleUserEntity: entity.isSingleUserEntity,
});

interface ICompanyInfo {
  name: string;
  vat: string;
}

interface IAddress {
  country: string;
  city: string;
  postalCode: string;
  street: string;
  contactEmail: string;
  contactName: string;
}

interface IEntity {
  companyInfo: ICompanyInfo;
  billingAddress: IAddress;
  isSingleUserEntity: boolean;
}

interface IState {
  entity: IEntity | null;
  accepted: boolean;
}

interface IProps extends WithStyles<typeof styles> {
  getProfile: () => {};
}

class TermsAndConditionsForm extends React.PureComponent<IProps> {
  public state: IState = {
    entity: null,
    accepted: false,
  };

  public componentDidMount() {
    if (isAdmin) {
      this.getEntity();
    }
  }

  public getEntity = async () => {
    const response = await httpService.get(
      process.env.REACT_APP_API_URL + 'entity/' + identity['nxt:entity:id'],
      { disableErrorMessageing: true }
    );
    this.setState({ entity: entityMapper(await response.json()) });
  };

  public updateTerms = async () => {
    const httpCallConfig = new HttpCallConfig();
    httpCallConfig.successMessage = 'Terms and conditions accepted.';
    await httpService.put(
      process.env.REACT_APP_API_URL + 'termsandconditions/',
      this.state,
      httpCallConfig
    );
    this.props.getProfile();
  };

  public getOnChangeEntity = (path: string) => {
    return (property: string) => {
      return (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
      ) => {
        const { entity } = this.state;
        if (!entity) {
          return null;
        }

        return this.setState({
          entity: {
            ...entity,
            [path]: {
              ...entity[path],
              [property]: event.target.value,
            },
          },
        });
      };
    };
  };

  public renderEntityFields() {
    const { classes } = this.props;
    const { entity } = this.state;

    if (!entity) {
      return null;
    }

    return (
      <div className={classes.fieldsContainer}>
        <Typography>Please make sure the info below is up to date. Fields marked with * are required.</Typography>
        <br />
        <Typography variant="subheading">
          <strong>Company info</strong>
        </Typography>
        <div className={classes.dropFields}>
          <TextField
            id="name"
            label="Name"
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
            required
            value={entity.companyInfo.name}
            onChange={this.getOnChangeEntity('companyInfo')('name')}
          />
          <TextField
            id="vat"
            label="VAT"
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
            required
            value={entity.companyInfo.vat}
            disabled={!entity.isSingleUserEntity}
            onChange={this.getOnChangeEntity('companyInfo')('vat')}
          />
        </div>
        <Typography variant="subheading">
          <strong>Billing address</strong>
        </Typography>
        <div>
          <FormControl required className={classes.textField}>
            <InputLabel shrink>Country</InputLabel>
            <Select
              name="country"
              native={true}
              value={entity.billingAddress.country}
              onChange={this.getOnChangeEntity('billingAddress')('country')}
            >
              {new CountryService().getCountries().map(item => (
                <option value={item.code} key={item.code}>
                  {item.name}
                </option>
              ))}
            </Select>
          </FormControl>
          <TextField
            id="city"
            label="City"
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
            required
            value={entity.billingAddress.city}
            onChange={this.getOnChangeEntity('billingAddress')('city')}
          />
          <TextField
            id="postalCode"
            label="Postal code"
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
            required
            value={entity.billingAddress.postalCode}
            onChange={this.getOnChangeEntity('billingAddress')('postalCode')}
          />
          <TextField
            id="street"
            label="Street"
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
            required
            value={entity.billingAddress.street}
            onChange={this.getOnChangeEntity('billingAddress')('street')}
          />
          <TextField
            id="contactName"
            label="Contact name"
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
            required
            value={entity.billingAddress.contactName}
            onChange={this.getOnChangeEntity('billingAddress')('contactName')}
          />
          <TextField
            id="contactEmail"
            label="Contact email"
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
            required
            value={entity.billingAddress.contactEmail}
            onChange={this.getOnChangeEntity('billingAddress')('contactEmail')}
          />
        </div>
      </div>
    );
  }

  public confirmConditionFulfilled = () => {
    const { entity, accepted } = this.state;
    if (entity) {
      if (Object.values(entity.billingAddress).some(value => !value)) {
        return false;
      }
      if (
        Object.entries(entity.companyInfo)
          .filter(([property]) => property !== 'vat' || entity.isSingleUserEntity)
          .some(([, value]) => !value)
      ) {
        return false;
      }
    }
    return accepted;
  };

  public render() {
    const { classes } = this.props;

    return (
      <Paper className={classes.tcModel}>
        <Typography>
          Our terms and conditions have changed. To continue, please read and accept{' '}
          <a href="https://www.nxtport.com/general-terms-conditions" target="_blank">
            NxtPort's general terms and conditions
          </a>
          .
        </Typography>
        {this.renderEntityFields()}
        <FormControlLabel
          control={
            <Checkbox
              checked={this.state.accepted}
              onChange={e => {
                this.setState({ accepted: e.target.checked });
              }}
              color="primary"
            />
          }
          label="I accept the new terms and conditions."
        />
        <div>
          <Button
            className={classes.tcButton}
            variant="outlined"
            color="primary"
            onClick={this.updateTerms}
            disabled={!this.confirmConditionFulfilled()}
          >
            Confirm
          </Button>
          <Button className={classes.tcButton} variant="outlined" color="primary" onClick={logout}>
            Logout
          </Button>
        </div>
      </Paper>
    );
  }
}

export default withStyles(styles)(TermsAndConditionsForm);



// WEBPACK FOOTER //
// ./src/modules/core/pages/TermsAndConditionsForm.tsx